import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail, makeBackendFileUrl } from "../lib/api";
import { useAuth } from "../lib/auth-context";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Calendar } from "../components/ui/calendar";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { LogOut, Loader2, Trash2, Upload, X, ChevronLeft, Star } from "lucide-react";
import { Logo } from "../lib/brand";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const [b, r, blk, rev] = await Promise.all([
        api.get("/admin/bookings"),
        api.get("/rooms"),
        api.get("/admin/blocked-dates"),
        api.get("/reviews"),
      ]);
      setBookings(b.data || []);
      setRooms(r.data || []);
      setBlocks(blk.data || []);
      setReviews(rev.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  return (
    <div className="min-h-screen bg-background paper-texture" data-testid="admin-dashboard">
      <header className="border-b border-border bg-background/95 backdrop-blur sticky top-0 z-20">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3">
            <Logo variant="light" className="h-9 w-auto" testId="admin-header-logo" />
            <span className="overline border-l border-border pl-3">Admin · Console</span>
          </Link>
          <div className="flex items-center gap-3 text-sm">
            <span className="font-sans text-muted-foreground hidden md:inline" data-testid="admin-user-email">
              {user?.email}
            </span>
            <Button variant="outline" className="rounded-full" onClick={logout} data-testid="logout-btn">
              <LogOut size={14} className="mr-1" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 md:px-12 max-w-7xl py-10">
        <Link to="/" className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-4" data-testid="admin-back-link">
          <ChevronLeft size={14} /> View site
        </Link>
        <h1 className="font-display text-4xl text-foreground mb-1">Welcome back</h1>
        <p className="font-sans text-sm text-muted-foreground mb-8">
          Manage bookings, room photos & prices, and block dates from here.
        </p>

        {loading ? (
          <div className="flex items-center gap-3 text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <Tabs defaultValue="bookings" className="w-full">
            <TabsList data-testid="admin-tabs" className="bg-card border border-border rounded-full p-1 h-auto">
              <TabsTrigger value="bookings" data-testid="tab-bookings" className="rounded-full px-5 py-2">
                Bookings ({bookings.length})
              </TabsTrigger>
              <TabsTrigger value="rooms" data-testid="tab-rooms" className="rounded-full px-5 py-2">
                Rooms
              </TabsTrigger>
              <TabsTrigger value="blocks" data-testid="tab-blocks" className="rounded-full px-5 py-2">
                Blocked Dates
              </TabsTrigger>
              <TabsTrigger value="reviews" data-testid="tab-reviews" className="rounded-full px-5 py-2">
                Reviews ({reviews.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="bookings" className="mt-6">
              <BookingsTab bookings={bookings} reload={reload} />
            </TabsContent>
            <TabsContent value="rooms" className="mt-6">
              <RoomsTab rooms={rooms} reload={reload} />
            </TabsContent>
            <TabsContent value="blocks" className="mt-6">
              <BlocksTab rooms={rooms} blocks={blocks} reload={reload} />
            </TabsContent>
            <TabsContent value="reviews" className="mt-6">
              <ReviewsTab rooms={rooms} reviews={reviews} reload={reload} />
            </TabsContent>
          </Tabs>
        )}
      </main>
    </div>
  );
}

function BookingsTab({ bookings, reload }) {
  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}/status`, null, { params: { status } });
      toast.success(`Marked ${status}`);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    }
  };

  if (bookings.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-border p-10 text-center text-muted-foreground font-sans" data-testid="bookings-empty">
        No bookings yet.
      </div>
    );
  }

  const badge = (s) => {
    const map = {
      pending: "bg-amber-100 text-amber-800 border-amber-200",
      advance_paid: "bg-emerald-100 text-emerald-800 border-emerald-200",
      completed: "bg-sky-100 text-sky-800 border-sky-200",
      cancelled: "bg-rose-100 text-rose-800 border-rose-200",
    };
    return `px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${map[s] || "bg-muted text-foreground"}`;
  };

  return (
    <div className="space-y-4" data-testid="bookings-list">
      {bookings.map((b) => (
        <Card key={b.id} data-testid={`booking-row-${b.id}`} className="rounded-2xl">
          <CardContent className="py-5 px-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-1">
                <span className="font-display text-lg">{b.room_name}</span>
                <span className={badge(b.status)} data-testid={`booking-status-${b.id}`}>{b.status.replace("_", " ")}</span>
              </div>
              <div className="text-sm text-muted-foreground font-sans">
                {b.guest_name} · {b.guest_email} · {b.guest_phone}
              </div>
              <div className="text-sm font-sans mt-1">
                {b.check_in} → {b.check_out} · {b.nights} night(s) · {b.guests} guest(s) · ₹{b.total_amount.toLocaleString("en-IN")} total · ₹{b.advance_amount.toLocaleString("en-IN")} advance
              </div>
              {b.notes && <div className="text-xs text-muted-foreground mt-1">Note: {b.notes}</div>}
            </div>
            <div className="flex gap-2 flex-wrap">
              <Select value={b.status} onValueChange={(v) => updateStatus(b.id, v)}>
                <SelectTrigger className="w-[170px]" data-testid={`status-select-${b.id}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="advance_paid">Advance paid</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function RoomsTab({ rooms, reload }) {
  return (
    <div className="space-y-6" data-testid="rooms-tab">
      {rooms.map((room) => (
        <RoomEditor key={room.id} room={room} reload={reload} />
      ))}
    </div>
  );
}

function RoomEditor({ room, reload }) {
  const [form, setForm] = useState({
    name: room.name,
    short: room.short,
    description: room.description,
    price_per_night: room.price_per_night,
    max_guests: room.max_guests,
    bedrooms: room.bedrooms,
    bathrooms: room.bathrooms,
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/rooms/${room.id}`, {
        ...form,
        price_per_night: Number(form.price_per_night),
        max_guests: Number(form.max_guests),
        bedrooms: Number(form.bedrooms),
        bathrooms: Number(form.bathrooms),
      });
      toast.success("Saved");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const data = new FormData();
    data.append("file", file);
    setUploading(true);
    try {
      await api.post(`/admin/rooms/${room.id}/photos`, data, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo uploaded");
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removePhoto = async (url) => {
    try {
      await api.delete(`/admin/rooms/${room.id}/photos`, { params: { url } });
      toast.success("Removed");
      reload();
    } catch (e) {
      toast.error("Remove failed");
    }
  };

  return (
    <Card data-testid={`room-editor-${room.slug}`} className="rounded-2xl">
      <CardHeader>
        <CardTitle className="font-display text-2xl">{room.name}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Name</Label>
            <Input data-testid={`room-${room.slug}-name`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <Label>Price per night (₹)</Label>
            <Input type="number" data-testid={`room-${room.slug}-price`} value={form.price_per_night} onChange={(e) => setForm({ ...form, price_per_night: e.target.value })} />
          </div>
          <div>
            <Label>Short tagline</Label>
            <Input value={form.short} onChange={(e) => setForm({ ...form, short: e.target.value })} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Guests</Label>
              <Input type="number" value={form.max_guests} onChange={(e) => setForm({ ...form, max_guests: e.target.value })} />
            </div>
            <div>
              <Label>Bedrooms</Label>
              <Input type="number" value={form.bedrooms} onChange={(e) => setForm({ ...form, bedrooms: e.target.value })} />
            </div>
            <div>
              <Label>Bathrooms</Label>
              <Input type="number" value={form.bathrooms} onChange={(e) => setForm({ ...form, bathrooms: e.target.value })} />
            </div>
          </div>
        </div>
        <div>
          <Label>Description</Label>
          <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>

        <div>
          <Label className="mb-2 block">Photos</Label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {(room.photos || []).map((url) => (
              <div key={url} className="relative group rounded-xl overflow-hidden border border-border aspect-[4/3]">
                <img src={makeBackendFileUrl(url)} alt="" className="w-full h-full object-cover" />
                <button
                  data-testid={`remove-photo-${room.slug}`}
                  onClick={() => removePhoto(url)}
                  className="absolute top-2 right-2 w-7 h-7 rounded-full bg-background/95 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="Remove photo"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
            <label className="aspect-[4/3] rounded-xl border-2 border-dashed border-border flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-primary text-muted-foreground hover:text-primary transition-colors" data-testid={`upload-photo-${room.slug}`}>
              {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload size={20} />}
              <span className="text-xs font-sans">{uploading ? "Uploading…" : "Upload photo"}</span>
              <input type="file" accept="image/*" className="hidden" onChange={handleUpload} disabled={uploading} />
            </label>
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} data-testid={`save-room-${room.slug}`} className="rounded-full bg-primary hover:bg-primary/90">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function BlocksTab({ rooms, blocks, reload }) {
  const [roomId, setRoomId] = useState(rooms[0]?.id || "");
  const [range, setRange] = useState({ from: undefined, to: undefined });
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const toISO = (d) => {
    if (!d) return null;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const da = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${da}`;
  };

  const add = async () => {
    if (!roomId) return toast.error("Select a room");
    if (!range.from || !range.to) return toast.error("Pick a date range");
    setSaving(true);
    try {
      await api.post("/admin/blocked-dates", {
        room_id: roomId,
        start_date: toISO(range.from),
        end_date: toISO(range.to),
        reason,
      });
      toast.success("Dates blocked");
      setRange({ from: undefined, to: undefined });
      setReason("");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/blocked-dates/${id}`);
      reload();
    } catch (e) {
      toast.error("Failed");
    }
  };

  return (
    <div className="grid md:grid-cols-2 gap-6" data-testid="blocks-tab">
      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="font-display text-2xl">Block Dates</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Room</Label>
            <Select value={roomId} onValueChange={setRoomId}>
              <SelectTrigger data-testid="block-room-select"><SelectValue placeholder="Select a room" /></SelectTrigger>
              <SelectContent>
                {rooms.map((r) => (
                  <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Calendar mode="range" selected={range} onSelect={(v) => setRange(v || { from: undefined, to: undefined })} numberOfMonths={1} data-testid="block-calendar" />
          <div>
            <Label>Reason (optional)</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Maintenance, owner stay, etc." />
          </div>
          <Button onClick={add} disabled={saving} data-testid="add-block-btn" className="rounded-full bg-primary hover:bg-primary/90 w-full">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Block dates
          </Button>
        </CardContent>
      </Card>

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="font-display text-2xl">Current Blocks</CardTitle>
        </CardHeader>
        <CardContent>
          {blocks.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="blocks-empty">No blocked dates.</p>
          ) : (
            <ul className="space-y-2">
              {blocks.map((b) => {
                const room = rooms.find((r) => r.id === b.room_id);
                return (
                  <li key={b.id} className="flex justify-between items-start border border-border rounded-xl p-3" data-testid={`block-row-${b.id}`}>
                    <div className="text-sm font-sans">
                      <div className="font-medium">{room?.name || b.room_id}</div>
                      <div className="text-muted-foreground">{b.start_date} → {b.end_date}</div>
                      {b.reason && <div className="text-xs text-muted-foreground mt-1">{b.reason}</div>}
                    </div>
                    <Button size="icon" variant="ghost" onClick={() => remove(b.id)} data-testid={`remove-block-${b.id}`}>
                      <Trash2 size={15} />
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ReviewsTab({ rooms, reviews, reload }) {
  const [form, setForm] = useState({ guest_name: "", rating: 5, text: "", stay_month: "", room_name: "" });
  const [saving, setSaving] = useState(false);

  const add = async () => {
    if (!form.guest_name.trim()) return toast.error("Guest name is required");
    if (!form.text.trim()) return toast.error("Review text is required");
    setSaving(true);
    try {
      await api.post("/admin/reviews", { ...form, rating: Number(form.rating) });
      toast.success("Review added");
      setForm({ guest_name: "", rating: 5, text: "", stay_month: "", room_name: "" });
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to add review");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/reviews/${id}`);
      toast.success("Review deleted");
      reload();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="grid md:grid-cols-2 gap-6" data-testid="reviews-tab">
      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="font-display text-2xl">Add a Review</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Guest name</Label>
            <Input data-testid="review-guest-name" value={form.guest_name} onChange={(e) => setForm({ ...form, guest_name: e.target.value })} placeholder="e.g., Priya & family" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Rating</Label>
              <Select value={String(form.rating)} onValueChange={(v) => setForm({ ...form, rating: Number(v) })}>
                <SelectTrigger data-testid="review-rating-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[5, 4, 3, 2, 1].map((n) => (
                    <SelectItem key={n} value={String(n)}>{n} star{n > 1 ? "s" : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Stay month (optional)</Label>
              <Input data-testid="review-stay-month" value={form.stay_month} onChange={(e) => setForm({ ...form, stay_month: e.target.value })} placeholder="e.g., May 2026" />
            </div>
          </div>
          <div>
            <Label>Room (optional)</Label>
            <Select value={form.room_name || "none"} onValueChange={(v) => setForm({ ...form, room_name: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="review-room-select"><SelectValue placeholder="Select room" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Not specified</SelectItem>
                {rooms.map((r) => (
                  <SelectItem key={r.id} value={r.name}>{r.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Review text</Label>
            <Textarea data-testid="review-text" rows={4} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} placeholder="What did the guest say about their stay?" />
          </div>
          <Button onClick={add} disabled={saving} data-testid="add-review-btn" className="rounded-full bg-primary hover:bg-primary/90 w-full">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Add review
          </Button>
        </CardContent>
      </Card>

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="font-display text-2xl">Published Reviews</CardTitle>
        </CardHeader>
        <CardContent>
          {reviews.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="reviews-empty">No reviews yet. Add your first guest review.</p>
          ) : (
            <ul className="space-y-3">
              {reviews.map((r) => (
                <li key={r.id} className="flex justify-between items-start border border-border rounded-xl p-4" data-testid={`admin-review-row-${r.id}`}>
                  <div className="text-sm font-sans flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold">{r.guest_name}</span>
                      <span className="flex gap-0.5">
                        {[1, 2, 3, 4, 5].map((i) => (
                          <Star key={i} size={12} className={i <= r.rating ? "fill-amber-400 text-amber-400" : "text-border"} />
                        ))}
                      </span>
                    </div>
                    <p className="text-muted-foreground line-clamp-2">{r.text}</p>
                    <p className="text-xs text-muted-foreground mt-1">{[r.room_name, r.stay_month].filter(Boolean).join(" · ")}</p>
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => remove(r.id)} data-testid={`delete-review-${r.id}`}>
                    <Trash2 size={15} />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
